import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { PasswordStrengthChecklist, validatePasswordPolicy } from '@/components/PasswordStrengthChecklist';
import {
  Plus, 
  Search, 
  RefreshCw, 
  Edit, 
  Trash2, 
  Shield,
  ShieldCheck,
  ShieldAlert,
  UserCog,
  Key,
  ToggleLeft,
  ToggleRight,
  Eye,
  EyeOff,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Protected account emails - must match backend
const MAIN_ADMIN_EMAIL = 'admin@cloudsetu.com';
const SUPPORT_ADMIN_EMAIL = 'support@cloudsetu.com';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-500', icon: ShieldCheck, description: 'Full system access' },
  level_3_support: { label: 'Level 3 Support', color: 'bg-blue-500', icon: Shield, description: 'Advanced operations' },
  level_2_support: { label: 'Level 2 Support', color: 'bg-cyan-500', icon: ShieldAlert, description: 'Standard support' },
  level_1_support: { label: 'Level 1 Support', color: 'bg-slate-500', icon: UserCog, description: 'Basic triage' }
};

const PERMISSION_MODULES = {
  dashboard: { label: 'Dashboard Access', icon: '📊' },
  products: { label: 'Products', icon: '📦' },
  categories: { label: 'Categories', icon: '🏷️' },
  partners: { label: 'Partners', icon: '🤝' },
  partners_kyc: { label: 'Partner KYC Verification', icon: '🛡️' },
  clients: { label: 'Clients', icon: '👥' },
  quotations: { label: 'Quotations', icon: '📑' },
  orders: { label: 'Orders', icon: '📋' },
  invoices: { label: 'Invoices', icon: '📄' },
  wallets: { label: 'Wallet Management', icon: '💰' },
  transactions: { label: 'Transactions', icon: '💳' },
  support: { label: 'Support', icon: '🎧' },
  activity_logs: { label: 'Activity Logs', icon: '📝' },
  notifications: { label: 'Notifications', icon: '🔔' },
  revenue: { label: 'Revenue', icon: '📈' },
  settings: { label: 'Settings', icon: '⚙️' },
  users: { label: 'Users', icon: '👤' }
};

export default function UserManagement() {
  const { canDelete, isMainAdmin, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Create user dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    name: '',
    role: 'level_1_support',
    phone: ''
  });
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Edit user dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Permissions dialog
  const [permOpen, setPermOpen] = useState(false);
  const [permUser, setPermUser] = useState(null);
  const [customPermissions, setCustomPermissions] = useState([]);
  const [savingPerm, setSavingPerm] = useState(false);
  
  // Password change dialog
  const [passOpen, setPassOpen] = useState(false);
  const [passUser, setPassUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [changingPass, setChangingPass] = useState(false);
  
  // Current user permissions
  const [myPermissions, setMyPermissions] = useState([]);
  const [myRole, setMyRole] = useState('');

  useEffect(() => {
    fetchMyPermissions();
    fetchRoles();
    fetchPermissions();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [page, roleFilter, searchQuery]);

  const fetchMyPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/my-permissions`);
      setMyPermissions(response.data.permissions || []);
      setMyRole(response.data.role || '');
    } catch (error) {
      console.error('Failed to fetch permissions');
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${API}/rbac/roles`);
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch roles');
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await axios.get(`${API}/rbac/permissions`);
      setAllPermissions(response.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch permissions');
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 20);
      if (roleFilter !== 'all') params.append('role', roleFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await axios.get(`${API}/rbac/users?${params.toString()}`);
      setUsers(response.data.users || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setCreating(true);
    try {
      await axios.post(`${API}/rbac/users`, newUser);
      toast.success('User created successfully');
      setCreateOpen(false);
      setNewUser({ email: '', password: '', name: '', role: 'level_1_support', phone: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!editUser) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/rbac/users/${editUser.id}`, {
        name: editUser.name,
        phone: editUser.phone,
        role: editUser.role
      });
      toast.success('User updated successfully');
      setEditOpen(false);
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await axios.post(`${API}/rbac/users/${user.id}/toggle-status`);
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle status');
    }
  };

  const handleDeleteUser = async (user) => {
    // Check if current user can delete
    if (!canDelete) {
      toast.error('Only Admin or Support Admin can delete users');
      return;
    }
    
    // Protect main admin and support admin
    const userEmail = user.email?.toLowerCase();
    if (userEmail === MAIN_ADMIN_EMAIL.toLowerCase()) {
      toast.error('Main Admin account cannot be deleted');
      return;
    }
    if (userEmail === SUPPORT_ADMIN_EMAIL.toLowerCase()) {
      toast.error('Support Admin account cannot be deleted');
      return;
    }
    
    // Support Admin cannot delete other admins
    const isCurrentUserSupportAdmin = currentUser?.email?.toLowerCase() === SUPPORT_ADMIN_EMAIL.toLowerCase();
    if (isCurrentUserSupportAdmin && user.role === 'admin') {
      toast.error('Support Admin cannot delete Admin accounts');
      return;
    }
    
    if (!confirm(`Are you sure you want to delete ${user.email}?`)) return;
    
    try {
      await axios.delete(`${API}/rbac/users/${user.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleChangePassword = async () => {
    if (!passUser || !newPassword) {
      toast.error('Password is required');
      return;
    }
    if (!validatePasswordPolicy(newPassword)) {
      toast.error('Password does not meet requirements');
      return;
    }
    
    setChangingPass(true);
    try {
      await axios.put(`${API}/rbac/users/${passUser.id}/password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setPassOpen(false);
      setPassUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPass(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    
    setSavingPerm(true);
    try {
      await axios.put(`${API}/rbac/users/${permUser.id}/permissions`, {
        permissions: customPermissions
      });
      toast.success('Permissions updated successfully');
      setPermOpen(false);
      setPermUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    } finally {
      setSavingPerm(false);
    }
  };

  const openPermissionsDialog = async (user) => {
    try {
      const response = await axios.get(`${API}/rbac/users/${user.id}`);
      setPermUser(response.data);
      setCustomPermissions(response.data.effective_permissions || []);
      setPermOpen(true);
    } catch (error) {
      toast.error('Failed to load user permissions');
    }
  };

  const togglePermission = (permission) => {
    setCustomPermissions(prev => 
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const canCreateRole = (role) => {
    if (myRole === 'admin') return true;
    if (myRole === 'level_3_support') {
      return ['level_1_support', 'level_2_support'].includes(role);
    }
    return false;
  };

  const hasPermission = (permission) => myPermissions.includes(permission);

  return (
    <DashboardLayout>
      <div className="h-full flex flex-col p-4 md:p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">User Management</h1>
            <p className="text-sm text-slate-500">Manage admin users and their permissions</p>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchUsers}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {hasPermission('users:create') && (
              <Button 
                size="sm" 
                onClick={() => setCreateOpen(true)}
                className="bg-blue-500 hover:bg-blue-600"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add User
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-[200px]">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600 text-nowrap">User</th>
                  <th className="text-left p-4 font-medium text-slate-600 text-nowrap">Role</th>
                  <th className="text-left p-4 font-medium text-slate-600 text-nowrap">Status</th>
                  <th className="text-left p-4 font-medium text-slate-600 text-nowrap">Created</th>
                  <th className="text-right p-4 font-medium text-slate-600 text-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto text-slate-400" />
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-slate-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  users.map(user => {
                    const roleConfig = ROLE_CONFIG[user.role] || {};
                    const RoleIcon = roleConfig.icon || UserCog;
                    return (
                      <tr key={user.id} className="border-b hover:bg-slate-50" data-testid={`user-row-${user.email}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full ${roleConfig.color || 'bg-slate-500'} flex items-center justify-center text-white font-semibold`}>
                              {user.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <div className="font-medium text-slate-800">{user.name}</div>
                              <div className="text-sm text-slate-500">{user.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2 text-nowrap">
                            <RoleIcon className="h-4 w-4 text-slate-500" />
                            <span className="font-medium">{roleConfig.label || user.role}</span>
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 text-nowrap">
                            {user.permissions_count} permissions
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                            {user.is_active !== false ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            {hasPermission('users:edit') && (
                              <>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setEditUser(user); setEditOpen(true); }}
                                  title="Edit User"
                                  data-testid={`edit-user-${user.email}`}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setPassUser(user); setPassOpen(true); }}
                                  title="Change Password"
                                >
                                  <Key className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleToggleStatus(user)}
                                  title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                                  disabled={user.email?.toLowerCase() === MAIN_ADMIN_EMAIL.toLowerCase()}
                                >
                                  {user.is_active !== false ? (
                                    <ToggleRight className="h-4 w-4 text-green-600" />
                                  ) : (
                                    <ToggleLeft className="h-4 w-4 text-slate-400" />
                                  )}
                                </Button>
                              </>
                            )}
                            {/* Manage Permissions: Only for main admin, not for admin account itself */}
                            {hasPermission('users:permissions') && isMainAdmin && 
                             user.email?.toLowerCase() !== MAIN_ADMIN_EMAIL.toLowerCase() && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openPermissionsDialog(user)}
                                title="Manage Permissions"
                                data-testid={`manage-permissions-${user.email}`}
                              >
                                <Shield className="h-4 w-4 text-purple-600" />
                              </Button>
                            )}
                            {/* Delete: Only for admin/support admin, not for protected accounts */}
                            {hasPermission('users:delete') && canDelete && 
                             user.email?.toLowerCase() !== MAIN_ADMIN_EMAIL.toLowerCase() &&
                             user.email?.toLowerCase() !== SUPPORT_ADMIN_EMAIL.toLowerCase() && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteUser(user)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                title="Delete User"
                                data-testid={`delete-user-${user.email}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t flex items-center justify-between">
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-slate-500">
                Page {page} of {totalPages}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>

        {/* Create User Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Plus className="h-5 w-5 text-blue-600" />
                Add New User
              </DialogTitle>
              <DialogDescription>Create a new admin user with specific role and permissions.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Name *</label>
                <Input
                  placeholder="Full name"
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Email *</label>
                <Input
                  type="email"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password *</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Minimum 8 characters"
                    value={newUser.password}
                    onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Phone (Optional)</label>
                <Input
                  placeholder="+91 9876543210"
                  value={newUser.phone}
                  onChange={(e) => setNewUser(prev => ({ ...prev, phone: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Role *</label>
                <Select 
                  value={newUser.role} 
                  onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                      canCreateRole(key) && (
                        <SelectItem key={key} value={key}>
                          <div className="flex items-center gap-2">
                            <config.icon className="h-4 w-4" />
                            <span>{config.label}</span>
                          </div>
                        </SelectItem>
                      )
                    ))}
                  </SelectContent>
                </Select>
                {ROLE_CONFIG[newUser.role] && (
                  <p className="text-xs text-slate-500 mt-1">
                    {ROLE_CONFIG[newUser.role].description}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateUser} disabled={creating} className="bg-blue-500 hover:bg-blue-600">
                {creating ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                Create User
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Edit User
              </DialogTitle>
              <DialogDescription>Update user information and role.</DialogDescription>
            </DialogHeader>
            {editUser && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={editUser.name}
                    onChange={(e) => setEditUser(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email</label>
                  <Input value={editUser.email} disabled className="bg-slate-50" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Phone</label>
                  <Input
                    value={editUser.phone || ''}
                    onChange={(e) => setEditUser(prev => ({ ...prev, phone: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Role</label>
                  <Select 
                    value={editUser.role} 
                    onValueChange={(v) => setEditUser(prev => ({ ...prev, role: v }))}
                    disabled={myRole !== 'admin' && myRole !== 'level_3_support'}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                        canCreateRole(key) && (
                          <SelectItem key={key} value={key}>
                            {config.label}
                          </SelectItem>
                        )
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateUser} disabled={saving} className="bg-blue-500 hover:bg-blue-600">
                {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={passOpen} onOpenChange={setPassOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Key className="h-5 w-5 text-orange-600" />
                Change Password
              </DialogTitle>
              <DialogDescription>
                Set a new password for {passUser?.email}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">New Password</label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min 8 chars, upper, lower, number, special"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <PasswordStrengthChecklist password={newPassword} />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPassOpen(false); setNewPassword(''); }}>Cancel</Button>
              <Button onClick={handleChangePassword} disabled={changingPass} className="bg-orange-500 hover:bg-orange-600">
                {changingPass ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Key className="h-4 w-4 mr-2" />}
                Change Password
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Permissions Dialog */}
        <Dialog open={permOpen} onOpenChange={setPermOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-purple-600" />
                Manage Permissions
              </DialogTitle>
              <DialogDescription>
                Customize permissions for {permUser?.email}. Uncheck to remove default permissions.
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <div className="rounded-lg p-3 mb-4 bg-[#2563eb]/10">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-medium">Current Role:</span>
                  <Badge className={ROLE_CONFIG[permUser?.role]?.color || 'bg-slate-500'}>
                    {ROLE_CONFIG[permUser?.role]?.label || permUser?.role}
                  </Badge>
                </div>
              </div>
              
              <div className="space-y-4" data-testid="permissions-module-list">
                {Object.entries(allPermissions).map(([module, perms]) => (
                  <div key={module} className="border rounded-lg p-4 mb-2" data-testid={`perm-module-${module}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">{PERMISSION_MODULES[module]?.icon || '📁'}</span>
                      <span className="font-medium">{PERMISSION_MODULES[module]?.label || module}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {perms.map(perm => (
                        <label 
                          key={perm.permission} 
                          className="flex items-center gap-2 p-2 rounded hover:bg-slate-50 cursor-pointer"
                          data-testid={`perm-label-${perm.permission}`}
                        >
                          <Checkbox
                            checked={customPermissions.includes(perm.permission)}
                            onCheckedChange={() => togglePermission(perm.permission)}
                            data-testid={`perm-checkbox-${perm.permission}`}
                          />
                          <span className="text-sm">{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-between items-center pt-4 border-t">
              <div className="text-sm text-slate-500" data-testid="permissions-selected-count">
                {customPermissions.length} permissions selected
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPermOpen(false)}>Cancel</Button>
                <Button onClick={handleSavePermissions} disabled={savingPerm} className="bg-purple-500 hover:bg-purple-600" data-testid="save-permissions-button">
                  {savingPerm ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
                  Save Permissions
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
